import { useState } from "react";
import { entityKey, RiskOperationalItem } from "./RiskOperationalOverview.types";

// Bulk-assess selection lives here, above both the tab-row entry button (in RiskArea) and the operational
// table/dialog, so the button and the table share one source of truth. Selection persists across pages/filters
// because it's keyed by type+id, independent of the currently paged result; the "select all" header and the
// header checkbox state act on whichever page items are passed in.
export function useBulkAssessSelection() {
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [isBulkAssessDialogOpen, setIsBulkAssessDialogOpen] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState<Map<string, RiskOperationalItem>>(new Map());

    const enterSelectionMode = () => setIsSelectionMode(true);
    const exitSelectionMode = () => {
        setIsSelectionMode(false);
        setSelectedEntities(new Map());
    };

    const isSelected = (item: RiskOperationalItem) => selectedEntities.has(entityKey(item.type, item.entityId));

    const toggleEntity = (item: RiskOperationalItem) => {
        setSelectedEntities((current) => {
            const next = new Map(current);
            const key = entityKey(item.type, item.entityId);
            if (next.has(key)) next.delete(key);
            else next.set(key, item);
            return next;
        });
    };

    const removeSelectedEntity = (key: string) => {
        setSelectedEntities((current) => {
            const next = new Map(current);
            next.delete(key);
            return next;
        });
    };

    const onSelectAllChanged = (pageItems: Array<RiskOperationalItem>) => {
        setSelectedEntities((current) => {
            const next = new Map(current);
            const allPageSelected = pageItems.every((item) => next.has(entityKey(item.type, item.entityId)));
            pageItems.forEach((item) => {
                const key = entityKey(item.type, item.entityId);
                if (allPageSelected) next.delete(key);
                else next.set(key, item);
            });
            return next;
        });
    };

    const getHeaderCheckBoxState = (pageItems: Array<RiskOperationalItem>): "true" | "false" | "indeterminate" => {
        const selectedOnPage = pageItems.filter((item) => selectedEntities.has(entityKey(item.type, item.entityId))).length;
        if (selectedOnPage === 0) return "false";
        return selectedOnPage === pageItems.length ? "true" : "indeterminate";
    };

    return {
        isSelectionMode,
        enterSelectionMode,
        exitSelectionMode,
        isSelected,
        toggleEntity,
        removeSelectedEntity,
        onSelectAllChanged,
        getHeaderCheckBoxState,
        selectedEntities: Array.from(selectedEntities.values()),
        selectedCount: selectedEntities.size,
        isBulkAssessDialogOpen,
        openBulkAssessDialog: () => setIsBulkAssessDialogOpen(true),
        closeBulkAssessDialog: () => setIsBulkAssessDialogOpen(false),
    };
}

export type BulkAssessSelection = ReturnType<typeof useBulkAssessSelection>;
